import type { RegisterSaleDTO } from '../dtos/sale/RegisterSaleDTO';
import type { SaleResponseDTO, SaleItemResponseDTO } from '../dtos/sale/SaleResponseDTO';
import type { IProductRepository } from '../../domain/interfaces';
import { MovementEntity } from '../../domain/entities/Movement';
import { MovementTypes, MovementStatusTypes } from '../../domain/shared/types';
import { InsufficientStockError } from '../../domain/shared/errors';
import type { IStockService } from '../../domain/services/interfaces';

interface IBulkMovementRepository {
  bulkSave(movements: MovementEntity[]): Promise<void>;
}

export interface RegisterSaleUseCase {
  execute(dto: RegisterSaleDTO): Promise<SaleResponseDTO>;
}

export default class RegisterSaleUseCaseImpl implements RegisterSaleUseCase {
  constructor(
    private readonly productRepository: IProductRepository,
    private readonly movementRepository: IBulkMovementRepository,
    private readonly stockService: IStockService,
  ) {}

  async execute(dto: RegisterSaleDTO): Promise<SaleResponseDTO> {
    // 1. Validate that all products exist and get their details
    const productDetails = await Promise.all(
      dto.items.map(async (item) => {
        const product = await this.productRepository.findById(item.productId);
        if (!product) {
          throw new Error(`Product not found: ${item.productId}`);
        }
        return {
          productId: item.productId,
          product,
          quantity: item.quantity,
          unitPrice: item.unitPrice ?? product.price.amount,
        };
      }),
    );

    // 2. Validate stock for ALL items BEFORE persisting
    for (const item of productDetails) {
      const available = await this.stockService.getAvailableStock(
        item.productId,
        dto.warehouseId,
      );
      if (available.value < item.quantity) {
        throw new InsufficientStockError(
          item.productId,
          item.quantity,
          available.value,
          dto.warehouseId,
        );
      }
    }

    // 3. Create all movement entities in memory
    const movements = productDetails.map((item) =>
      MovementEntity.create({
        movementType: MovementTypes.OUT,
        productId: item.productId,
        warehouseId: dto.warehouseId,
        quantity: { value: item.quantity, unit: 'units' },
        unitCost: { amount: item.product.cost.amount, currency: item.product.cost.currency },
        unitPrice: {
          amount: item.unitPrice,
          currency: item.product.price.currency,
        },
        status: MovementStatusTypes.COMPLETED,
        initiatedBy: 'system',
        completedBy: 'system',
        reference: { type: 'SO', number: dto.referenceNumber },
        notes: dto.notes || undefined,
      }),
    );

    // 4. Persist ALL movements atomically
    await this.movementRepository.bulkSave(movements);

    // 5. Build response
    const saleItems: SaleItemResponseDTO[] = productDetails.map((item) => ({
      productId: item.productId,
      productName: item.product.name,
      productSku: item.product.sku,
      quantity: item.quantity,
      unitPrice: item.unitPrice,
    }));

    const totalItems = saleItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = saleItems.reduce(
      (sum, item) => sum + item.quantity * item.unitPrice,
      0,
    );

    return {
      referenceNumber: dto.referenceNumber,
      date: new Date(),
      warehouseId: dto.warehouseId,
      items: saleItems,
      totalItems,
      totalAmount,
      status: 'completed',
      movementIds: movements.map((m) => m._id),
      createdAt: new Date(),
    };
  }
}
